import React, { useEffect, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useAppSelector, useAppDispatch } from "../../../store/hooks.ts";

import {
	selectReportStartDate,
	selectReportEndDate,
} from "../../../store/slices/reportsSlice.ts";
import { selectAnchorDate } from "../../../store/slices/calendarSlice.ts";
import { calculateFeeEstimate } from "../../calendar/helper/feeCalculation.ts";
import {
	parseISO,
	startOfWeek,
	differenceInCalendarWeeks,
	isValid,
} from "date-fns";

// ⬇️ schedules slice helpers
import {
	selectAllSchedules,
	fetchSchedulesIfNeeded,
} from "../../../store/slices/schedulesSlice.ts";

type Cycle = "A" | "B" | "C" | "D";
type SessionLabel = "AM" | "PM" | "TW";

const CYCLES: Cycle[] = ["A", "B", "C", "D"];
const weeks = ["Week A", "Week B", "Week C", "Week D"];
const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const sessions: SessionLabel[] = ["AM", "PM", "TW"];

// --- colour helpers (no echarts dependency) ---
function hexToRgb(hex: string) {
	const m = hex.replace("#", "");
	const bigint = parseInt(
		m.length === 3
			? m
					.split("")
					.map((c) => c + c)
					.join("")
			: m,
		16
	);
	return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
function rgbToHex(r: number, g: number, b: number) {
	const toHex = (n: number) => n.toString(16).padStart(2, "0");
	return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(
		Math.round(b)
	)}`;
}
function colourLerp(a: string, b: string, t: number) {
	const c1 = hexToRgb(a),
		c2 = hexToRgb(b);
	const r = c1.r + (c2.r - c1.r) * t;
	const g = c1.g + (c2.g - c1.g) * t;
	const b2 = c1.b + (c2.b - c1.b) * t;
	return rgbToHex(r, g, b2);
}

function parseAnchorFlexible(anchor: any): Date {
	if (anchor instanceof Date) return anchor;
	const s = String(anchor || "").trim();
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return parseISO(`${s}T00:00:00`);
	const normalised = s.includes("T") ? s : s.replace(" ", "T");
	return new Date(normalised);
}

function toCycle(dateISO: string, anchorYMD: string): "A" | "B" | "C" | "D" {
	const d = parseISO(dateISO);
	const a = parseAnchorFlexible(anchorYMD);

	if (!isValid(d) || !isValid(a)) return "A";

	const dMon = startOfWeek(d, { weekStartsOn: 1 });
	const aMon = startOfWeek(a, { weekStartsOn: 1 });
	const diff = differenceInCalendarWeeks(dMon, aMon, { weekStartsOn: 1 });
	const idx = ((diff % 4) + 4) % 4;
	return CYCLES[idx];
}

function weekdayIndex(dateISO: string): number {
	return (parseISO(dateISO).getDay() + 6) % 7; // 0=Mon..6=Sun
}

/** AM if < "11:30", PM otherwise, TW fallback */
function classifySessionFlexible(t?: string | null): SessionLabel {
	if (!t) return "TW";
	const m = String(t).match(/^(\d{1,2}):(\d{1,2})$/);
	const hhmm = m ? `${m[1].padStart(2, "0")}:${m[2].padStart(2, "0")}` : t;
	return hhmm < "11:30" ? "AM" : "PM";
}

/** Extract item numbers (strings) from a sub-event's actual/proposed fields */
function extractItemNums(ev: any): string[] {
	const grab = (raw: any): number[] => {
		if (!raw || raw === "null" || raw === "[]") return [];
		if (Array.isArray(raw))
			return raw.map(Number).filter((n) => Number.isFinite(n));
		if (typeof raw === "string") {
			try {
				const arr = JSON.parse(raw);
				if (Array.isArray(arr))
					return arr.map(Number).filter((n) => Number.isFinite(n));
			} catch {
				// loose parse
				return raw
					.split(/[^0-9]+/)
					.map((t) => Number(t))
					.filter((n) => Number.isFinite(n));
			}
		}
		return [];
	};
	const a = grab(ev?.actual_items);
	const p = grab(ev?.proposed_items);
	return Array.from(new Set([...a, ...p])).map(String);
}

const AveragePerSession: React.FC = () => {
	const dispatch = useAppDispatch();

	const chartRef = useRef<ReactECharts>(null);
	const hostRef = useRef<HTMLDivElement>(null);

	const startISO = useAppSelector(selectReportStartDate);
	const endISO = useAppSelector(selectReportEndDate);
	const anchorYMD = useAppSelector(selectAnchorDate);
	const sessionsArr = useAppSelector((s) => s.calendar.sessions);
	const userSettings = useAppSelector((s) => (s as any)?.user?.settings);
	const schedules = useAppSelector(selectAllSchedules); // ⬅️ correct source

	// Prefetch any missing schedules for sessions in the selected window.
	useEffect(() => {
		if (!Array.isArray(sessionsArr) || sessionsArr.length === 0) return;

		let s = startISO?.slice(0, 10);
		let e = endISO?.slice(0, 10);
		if (s && e && s > e) [s, e] = [e, s];

		const itemSet = new Set<string>();
		for (const sess of sessionsArr as any[]) {
			const dateISO: string | undefined =
				sess.start ||
				(sess.date_of_service && `${sess.date_of_service}T00:00:00`) ||
				(sess.date && `${sess.date}T00:00:00`);
			if (!dateISO) continue;

			const ymd = dateISO.slice(0, 10);
			if (s && ymd < s) continue;
			if (e && ymd > e) continue;

			const subEvents = Array.isArray(sess.events) ? sess.events : [];
			for (const ev of subEvents) {
				extractItemNums(ev).forEach((n) => itemSet.add(n));
			}
		}

		const wanted = Array.from(itemSet);
		if (wanted.length) {
			// fire and forget; the selector will update when data arrives
			dispatch(fetchSchedulesIfNeeded(wanted));
		}
	}, [dispatch, sessionsArr, startISO, endISO]);

	// ---------- MAIN AGGREGATION ----------
	const myData = useMemo(() => {
		if (
			!anchorYMD ||
			!Array.isArray(sessionsArr) ||
			sessionsArr.length === 0
		) {
			return null as number[][][] | null;
		}

		let s = startISO?.slice(0, 10);
		let e = endISO?.slice(0, 10);
		if (s && e && s > e) [s, e] = [e, s];

		const sums: number[][][] = Array.from({ length: 4 }, () =>
			Array.from({ length: 3 }, () => Array(7).fill(0))
		);
		const counts: number[][][] = Array.from({ length: 4 }, () =>
			Array.from({ length: 3 }, () => Array(7).fill(0))
		);

		for (const sess of sessionsArr as any[]) {
			const dateISO: string | undefined =
				sess.start ||
				(sess.date_of_service && `${sess.date_of_service}T00:00:00`) ||
				(sess.date && `${sess.date}T00:00:00`);
			if (!dateISO) continue;

			const ymd = dateISO.slice(0, 10);
			if (s && ymd < s) continue;
			if (e && ymd > e) continue;

			const cyc = toCycle(dateISO, anchorYMD);
			const weekIdx = CYCLES.indexOf(cyc);
			if (weekIdx < 0) continue;

			const dayIdx = weekdayIndex(dateISO);
			const slot = classifySessionFlexible(
				sess.startingTime ?? sess.start_time
			);
			const sessionIdx = sessions.indexOf(slot);
			if (sessionIdx < 0) continue;

			const subEvents = Array.isArray(sess.events) ? sess.events : [];
			if (!subEvents.length) continue; // no session exists in this slot

			// Prefer inline (calendar) per-patient feeEstimate; fallback to helper.
			const hasInline = subEvents.some((se: any) =>
				Number.isFinite(Number(se?.feeEstimate))
			);
			const estimate = hasInline
				? subEvents.reduce(
						(acc: number, se: any) =>
							acc + (Number(se?.feeEstimate) || 0),
						0
				  )
				: Number(
						calculateFeeEstimate(
							userSettings,
							subEvents,
							schedules
						) || 0
				  );

			// Average only across real sessions (don’t include blanks)
			sums[weekIdx][sessionIdx][dayIdx] += estimate;
			counts[weekIdx][sessionIdx][dayIdx] += 1;
		}

		// Convert to averages
		const avgs: number[][][] = sums.map((week, w) =>
			week.map((sessRows, si) =>
				sessRows.map((sum, dIdx) => {
					const n = counts[w][si][dIdx] || 0;
					return n ? sum / n : 0;
				})
			)
		);
		return avgs;
	}, [sessionsArr, anchorYMD, startISO, endISO, userSettings, schedules]);

	// ---------- BUILD ECHARTS OPTION (unchanged look) ----------
	const option = useMemo(() => {
		if (!myData) return {};

		const yLabels: {
			week: string;
			session: SessionLabel;
			label: string;
		}[] = [];
		weeks.forEach((week) =>
			sessions.forEach((s) =>
				yLabels.push({ week, session: s, label: `${week} ${s}` })
			)
		);

		const sessionData: [number, number, number][] = [];
		for (let w = 0; w < weeks.length; w++) {
			for (let si = 0; si < sessions.length; si++) {
				for (let d = 0; d < days.length; d++) {
					sessionData.push([d, w * 3 + si, myData[w][si][d] || 0]);
				}
			}
		}

		const yLabelsRev = [...yLabels].reverse();
		const sessionDataRev = sessionData.map(([d, y, v]) => [
			d,
			yLabels.length - 1 - y,
			v,
		]) as [number, number, number][];

		const weeklySums = weeks.map((_, weekIdx) => {
			const indices = [0, 1, 2].map(
				(i) => yLabels.length - 1 - (weekIdx * 3 + i)
			);
			const total = sessionDataRev
				.filter((d) => indices.includes(d[1]))
				.reduce((sum, d) => sum + (d[2] || 0), 0);
			return total.toFixed(2);
		});

		const getColour = (val: number) => {
			if (val === 0) return "#f0f0f0";
			const ratio = Math.max(0, Math.min(1, (val - 500) / 500)); // 500→1000
			return colourLerp("#FFD1BA", "#A3C8F0", ratio);
		};

		return {
			tooltip: {
				formatter: (p: any) => {
					if (p.seriesIndex === 0) {
						const yIdx = p.value[1];
						const origIdx = yLabels.length - 1 - yIdx;
						const weekIdx = Math.floor(origIdx / 3);
						const sessionIdx = origIdx % 3;
						const val = Number(p.value[2] || 0);
						return `${weeks[weekIdx]} ${days[p.value[0]]} (${
							sessions[sessionIdx]
						}): $${val.toFixed(2)} AUD`;
					} else if (p.seriesIndex === 1) {
						const weekIdx = Number(p.data?.value ?? 0);
						return `${weeks[weekIdx]} Total: $${weeklySums[weekIdx]} AUD`;
					}
					return "";
				},
			},
			grid: { left: 180, right: 120, top: 60, bottom: 40 },
			xAxis: {
				type: "category",
				data: days.concat(["Total"]),
				position: "top",
				axisTick: { show: false },
				splitLine: { show: true },
			},
			yAxis: {
				type: "category",
				data: yLabelsRev.map((y) => y.session),
				axisTick: { show: false },
				axisLabel: { show: true, fontSize: 12 },
				splitLine: { show: true },
			},
			series: [
				{
					type: "custom",
					renderItem: (params: any, api: any) => {
						const dayIdx = api.value(0);
						const yIdx = api.value(1);
						const value = api.value(2);
						const [x, y] = api.coord([dayIdx, yIdx]);
						const cellWidth = api.size([1, 0])[0];
						const cellHeight = api.size([0, 1])[1];
						return {
							type: "rect",
							shape: {
								x: x - cellWidth / 2 + 1,
								y: y - cellHeight / 2 + 1,
								width: cellWidth - 2,
								height: cellHeight - 2,
								r: 4,
							},
							style: {
								fill: getColour(value),
								stroke: "#fff",
								text:
									value > 0
										? `$${Number(value).toFixed(0)}`
										: "",
								textFill: "#000",
								fontSize: 10,
								textPosition: "inside",
							},
						};
					},
					data: sessionDataRev,
				},
				{
					type: "custom",
					renderItem: (params: any, api: any) => {
						const weekIdx = api.value(0);
						const yStart = yLabels.length - 1 - (weekIdx * 3 + 2);
						const [x, yCenter] = api.coord([
							days.length,
							yStart + 1,
						]);
						const cellWidth = api.size([1, 0])[0];
						const cellHeight = api.size([0, 1])[1] * 3;
						return {
							type: "rect",
							shape: {
								x: x - cellWidth / 2 + 1,
								y: yCenter - cellHeight / 2 + 1,
								width: cellWidth - 2,
								height: cellHeight - 2,
								r: 6,
							},
							style: {
								fill: "#e0e0e0",
								stroke: "#ccc",
								text: `${weeks[weekIdx]} Total\n$${weeklySums[weekIdx]} AUD`,
								textFill: "#000",
								fontSize: 11,
								textPosition: "inside",
							},
						};
					},
					data: weeks.map((w, i) => ({ value: i, name: w })),
				},
				{
					type: "custom",
					renderItem: (params: any, api: any) => {
						const weekIdx = api.value(0);
						const yStart = yLabels.length - 1 - weekIdx * 3;
						const yEnd = yLabels.length - 1 - (weekIdx * 3 + 2);
						const cellWidth = 60;
						const blockHeight = api.size([0, 1])[1] * 3;
						const coord = api.coord([0, (yStart + yEnd) / 2]);
						const y = coord[1];
						const yTop =
							api.coord([0, yStart])[1] - api.size([0, 1])[1] / 2;
						const yBottom =
							api.coord([0, yEnd])[1] + api.size([0, 1])[1] / 2;
						const lastCol = days.length;
						const [xLineEnd] = api.coord([lastCol, 0]);
						const extra = cellWidth / 2 + 8;
						return {
							type: "group",
							children: [
								{
									type: "rect",
									shape: {
										x: 40,
										y: y - blockHeight / 2,
										width: cellWidth,
										height: blockHeight,
										r: [6, 0, 0, 6],
									},
									style: {
										fill: "#f2f2f2",
										stroke: "#999",
										text: weeks[weekIdx],
										textFill: "#000",
										fontSize: 13,
										textPosition: "inside",
									},
								},
								{
									type: "line",
									shape: {
										x1: 40,
										y1: yTop,
										x2: xLineEnd + extra,
										y2: yTop,
									},
									style: { stroke: "#bbb", lineWidth: 2 },
								},
								{
									type: "line",
									shape: {
										x1: 40,
										y1: yBottom,
										x2: xLineEnd + extra,
										y2: yBottom,
									},
									style: { stroke: "#bbb", lineWidth: 2 },
								},
							],
						};
					},
					data: weeks.map((w, i) => ({ value: i, name: w })),
				},
			],
		};
	}, [myData]);

	useEffect(() => {
		const resize = () => {
			try {
				chartRef.current?.getEchartsInstance().resize();
			} catch {}
		};
		const el = hostRef.current;
		window.addEventListener("resize", resize);
		if (!el) return () => window.removeEventListener("resize", resize);
		const ro = new ResizeObserver(() => resize());
		ro.observe(el);
		resize();
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", resize);
		};
	}, []);

	if (!myData) {
		return (
			<Card sx={{ height: 360 }}>
				<CardHeader title="Average per Session (4-week cycle)" />
				<CardContent
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: 300,
					}}
				>
					<Typography>No data to display.</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card ref={hostRef} sx={{ width: "100%" }}>
			<CardHeader
				title="Average per Session (4-week cycle)"
				subheader={`Anchor (Week A): ${
					anchorYMD || "not set"
				} • Values in AUD • Sessions: AM, PM, TW`}
			/>
			<CardContent sx={{ p: 0 }}>
				<ReactECharts
					ref={chartRef}
					option={option}
					notMerge
					lazyUpdate
					style={{ width: "100%", height: "680px" }}
					onChartReady={(inst) => inst.resize()}
				/>
			</CardContent>
		</Card>
	);
};

export default AveragePerSession;
